import type { Total } from '@/js/types/cart.ts';
import type { DadataParams, Suggestion } from '@/js/types/dadata.ts';
import type { Option } from '@/js/types/options.ts';
import type { DeliveryType, OrderRequest, PaymentMethod } from '@/js/types/order.ts';
import { clearPhone } from '@/js/helper/clear-phone.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';

export const DELIVERY_TIMES = [
  '10:00 - 12:00',
  '12:00 - 14:00',
  '14:00 - 16:00',
  '16:00 - 18:00',
  '18:00 - 20:00',
];
export const CALL_TIMES = [
  getLocaleValue('within_10_minutes'),
  getLocaleValue('within_20_minutes'),
  getLocaleValue('within_30_minutes'),
];
export const FLOORS = [
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
];

export const MOSCOW_REGION = 'Москва';
export const MOSKOVSKAYA_REGION = 'Московская';

export function getDadataCityParams(): DadataParams {
  return {
    to_bound: { value: 'city' },
    from_bound: { value: 'city' },
  };
}

export const PRODUCT_WORDS = ['товар', 'товара', 'товаров'] as const;

const TOTAL_NAME: Partial<Record<Total['type'], string>> = {
  sum: getLocaleValue('cost_of_goods'),
  sum_old: getLocaleValue('cost_of_goods_without_discount'),
};

export function getTotalName(type: Total['type']) {
  return TOTAL_NAME[type] ?? '';
}

export function getDeliveryMethods(isMoscow: boolean): { value: DeliveryType, label: string }[] {
  return isMoscow
    ? [
        {
          value: 'pickup',
          label: 'Самовывоз',
        },
        {
          value: 'tk_terminal',
          label: 'До транспортной компании',
        },
        {
          value: 'self',
          label: 'Доставка по Москве и области',
        },
      ]
    : [
        {
          value: 'tk_terminal',
          label: 'До транспортной компании',
        },
      ];
}

export function suggestToCity(suggest: Suggestion) {
  let town = '';
  const {
    region_with_type: region,
    area_with_type: area,
    city_with_type: city,
  } = suggest.data;
  if (region && region !== city) {
    town += `${region}, `;
  }
  if (area !== null) {
    town += `${area}, `;
  }
  town += city;
  return town;
}

export function getPaymentMethods(): { value: PaymentMethod, label: string }[] {
  return [
    // {
    //   value: 'sber',
    //   label: 'Сбербанк',
    // },
    {
      value: 'tbank_card',
      label: getLocaleValue('tbank_card_label'),
    },
    // {
    //   value: 'tbank_credit',
    //   label: 'Тбанк кредит',
    // },
    {
      value: 'transfer',
      label: getLocaleValue('transfer_label'),
    },
  ];
}

export function getDeliveryTimes(): Option[] {
  return DELIVERY_TIMES.map(item => ({ label: item, value: item }));
}

export function getFloors(): Option[] {
  return FLOORS.map(floor => ({
    label: `${floor}-й этаж`,
    value: floor,
  }));
}

export function getCallTimes(): Option[] {
  return CALL_TIMES.map(item => ({
    label: item,
    value: item,
  }));
}

export function cleanOrderFields(data: OrderRequest) {
  let order: OrderRequest = {
    name: data.name,
    email: data.email,
    phone: clearPhone(data.phone),
    legal_type: data.legal_type,
    delivery_type: data.delivery_type,
    delivery_data: data.delivery_data,
    payment_method: data.payment_method,
  };

  if (data.legal_type === 'organization') {
    order = {
      ...order,
      legal_data: data.legal_data,
    };
  }

  if (data.lift_data && data.lift_data.lift_level && data.lift_data.lift_price) {
    order = {
      ...order,
      lift_data: data.lift_data,
    };
  }

  if (data.payment_data) {
    if (data.payment_data.bonus !== null) {
      order = {
        ...order,
        payment_data: {
          bonus: data.payment_data.bonus,
        },
      };
    }
    if (data.payment_data.coupon !== null) {
      order = {
        ...order,
        payment_data: {
          ...order.payment_data,
          coupon: data.payment_data.coupon,
        },
      };
    }
  }

  if (data.additional_data && data.additional_data.call_time) {
    order = {
      ...order,
      additional_data: data.additional_data,
    };
  }

  if (data.services && data.services.installment_items.length > 0) {
    order = {
      ...order,
      services: data.services,
    };
  }

  return order;
}
